<template>
<BaseTemplate app-name="RIS System" page-title="">
  <template #content>
    <div
      class="h-screen flex flex-col bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-100 transition-colors duration-200"
    >
      <!-- Banner Message -->
      <BannerMessage 
          v-if="showBanner"
          :type="bannerType"
          :message="bannerMessage"
          @close="showBanner = false"
      />

      <!-- Main Content -->
      <div class="flex-1 flex overflow-hidden">
        <!-- Content Area -->
        <div class="flex-1 overflow-hidden flex flex-col">
          <!-- Header -->
          <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 shadow-sm">
            <div class="flex flex-wrap justify-between items-center gap-4">
              <div class="flex items-center gap-3">
                <h2 class="text-xl font-medium" :style="{ color: 'var(--clinic-font-color)' }">Configurações da Clínica</h2>
              </div>
              <div class="flex items-center gap-2">

              </div>
            </div>
          </div>

          <!-- Clinic Settings Content -->
          <div class="flex-1 p-4 bg-gray-50 dark:bg-gray-900">
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-700">
              <!-- Tabs Navigation -->
              <div class="border-b border-gray-200 dark:border-gray-700">
                <div class="flex overflow-x-auto">
                  <button
                    v-for="tabItem in tabs"
                    :key="tabItem.id"
                    @click="activeTab = tabItem.id"
                    class="px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors focus:outline-none"
                    :class="{
                      'border-b-2': activeTab === tabItem.id,
                      'border-b-2': activeTab !== tabItem.id
                    }"
                    :style="{
                      color: activeTab === tabItem.id ? 'var(--clinic-icon-color)' : 'var(--clinic-font-color)',
                      borderColor: activeTab === tabItem.id ? 'var(--clinic-icon-color)' : 'transparent'
                    }"
                  >
                    <div class="flex items-center gap-2">
                      <component :is="tabItem.icon" class="w-4 h-4" />
                      {{ tabItem.name }}
                    </div>
                  </button>
                </div>
              </div>

              <!-- Tab Content -->
              <div class="p-6">
                <!-- Error Message -->
                <div v-if="errorMessage" class="mb-4 p-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-md">
                  <p class="text-red-800 dark:text-red-400 text-sm">{{ errorMessage }}</p>
                </div>

                <!-- Banner de confirmação de reset -->
                <div v-if="showResetBanner" class="mb-4 p-3 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-md flex items-center gap-2">
                  <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" /></svg>
                  <span class="text-green-800 dark:text-green-400 text-sm">Estilização resetada para o padrão com sucesso!</span>
                </div>

                <!-- Mensagem de permissão -->
                <div v-if="!canEdit && canView" class="mb-4 p-4 bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 rounded-md">
                  <p class="text-blue-800 dark:text-blue-400 text-sm">
                    <svg class="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Você tem permissão apenas para visualizar as configurações da clínica. Para editar, entre em contato com o administrador do sistema.
                  </p>
                </div>

                <!-- ABA: Clínica -->
                <div v-if="activeTab === 'clinica'" class="space-y-6">
                  <!-- Dados da Clínica -->
                  <div>
                    <h3 class="text-lg font-medium mb-4" :style="{ color: 'var(--clinic-font-color)' }">Dados da Clínica</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div class="md:col-span-2">
                        <label for="nome" class="block text-sm font-medium mb-1 input-font-color">Nome da Clínica*</label>
                        <input
                          id="nome"
                          type="text"
                          v-model="clinic.nome"
                          required
                          :readonly="!canEdit"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 input-font-color"
                          :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }"
                        />
                      </div>
                      
                      <div class="md:col-span-2">
                        <label for="endereco" class="block text-sm font-medium mb-1 input-font-color">Endereço da Clínica*</label>
                        <input
                          id="endereco"
                          type="text"
                          v-model="clinic.endereco"
                          required
                          :readonly="!canEdit"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 input-font-color"
                          :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }"
                        />
                      </div>
                      
                      <div>
                        <label for="telefone" class="block text-sm font-medium mb-1 input-font-color">Telefone</label>
                        <input
                          id="telefone"
                          type="text"
                          v-model="clinic.telefone"
                          placeholder="(00) 0000-0000"
                          :readonly="!canEdit"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 input-font-color"
                          :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }"
                        />
                      </div>
                      
                      <div>
                        <label for="cnes" class="block text-sm font-medium mb-1 input-font-color">CNES da Clínica</label>
                        <input
                          id="cnes"
                          type="text"
                          v-model="clinic.cnes"
                          :readonly="!canEdit"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 input-font-color"
                          :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }"
                        />
                      </div>
                      
                      <div>
                        <label for="sigla" class="block text-sm font-medium mb-1 input-font-color">Sigla</label>
                        <input
                          id="sigla"
                          type="text"
                          v-model="clinic.sigla"
                          :readonly="!canEdit"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 input-font-color"
                          :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }"
                        />
                      </div>
                      
                      <div class="md:col-span-2">
                        <label for="url_portal_paciente" class="block text-sm font-medium mb-1 input-font-color">URL Portal do Paciente</label>
                        <input
                          id="url_portal_paciente"
                          type="url"
                          v-model="clinic.url_portal_paciente"
                          placeholder="https://portal.minhaClinica.com.br"
                          :readonly="!canEdit"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 input-font-color"
                          :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ABA: Estilização -->
                <div v-if="activeTab === 'estilizacao'" class="space-y-4">
                  <div class="flex justify-end mb-2">
                    <button
                      v-if="canEdit"
                      class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-md text-xs font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      @click="confirmResetStyle"
                    >
                      Resetar estilização
                    </button>
                  </div>
                  <!-- Área das logos -->
                  <div class="mb-6">
                    <div class="flex flex-col md:flex-row gap-6 justify-center items-start w-full">
                      <!-- Logo do Sistema -->
                      <div class="flex flex-col items-center w-full md:w-auto">
                        <label class="block text-sm font-medium mb-1 input-font-color">Logo do Sistema (BaseTemplate)</label>
                        <div class="border-2 border-dashed border-gray-400 dark:border-gray-600 rounded-xl flex items-center justify-center w-[140px] h-[140px] mb-2 bg-gray-900/30 dark:bg-gray-800/30 relative overflow-hidden">
                          <img :src="baseTemplateLogoSrc" alt="Logo" class="max-w-[120px] max-h-[120px] object-contain rounded shadow" />
                          <input v-if="canEdit" ref="logoInputRef" type="file" class="hidden" @change="onLogoChange" accept="image/*" />
                          <button v-if="canEdit" class="absolute inset-0 w-full h-full opacity-0 cursor-pointer" @click="logoInputRef.click()"></button>
                        </div>
                        <div class="text-xs text-center text-gray-400 dark:text-gray-500">
                          <span v-if="canEdit">Arraste a imagem aqui ou <span class="text-pink-400 underline cursor-pointer import-link" @click="logoInputRef.click()">clique para importar</span></span>
                          <span v-else>Logo do sistema</span>
                        </div>
                      </div>
                      <!-- Logo do Login -->
                      <div class="flex flex-col items-center w-full md:w-auto">
                        <label class="block text-sm font-medium mb-1 input-font-color">Logo da Tela de Login</label>
                        <div class="border-2 border-dashed border-gray-400 dark:border-gray-600 rounded-xl flex items-center justify-center w-[140px] h-[140px] mb-2 bg-gray-900/30 dark:bg-gray-800/30 relative overflow-hidden">
                          <img :src="loginLogoSrc" alt="Logo do Login" class="max-w-[120px] max-h-[120px] object-contain rounded shadow" />
                          <input v-if="canEdit" ref="loginLogoInputRef" type="file" class="hidden" @change="onLoginLogoChange" accept="image/*" />
                          <button v-if="canEdit" class="absolute inset-0 w-full h-full opacity-0 cursor-pointer" @click="loginLogoInputRef.click()"></button>
                        </div>
                                                  <div class="text-xs text-center text-gray-400 dark:text-gray-500">
                            <span v-if="canEdit">Arraste a imagem aqui ou <span class="text-pink-400 underline cursor-pointer import-link" @click="loginLogoInputRef.click()">clique para importar</span></span>
                            <span v-else>Logo da tela de login</span>
                          </div>
                      </div>
                    </div>
                  </div>
                  <!-- Área das cores -->
                  <div class="flex flex-col md:flex-row gap-4">
                    <!-- Modo Claro -->
                    <div class="flex-1 mode-card p-4 overflow-y-auto max-h-[70vh] min-w-[320px]">
                      <div class="sticky top-0 z-10 pb-2 mb-2">
                        <h3 class="mode-title">Modo Claro</h3>
                      </div>
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
                        <div class="flex items-center gap-2">
                          <label class="w-32 text-xs font-medium input-font-color">Cor do Botão</label>
                          <input type="color" v-model="clinic.cor_botao" :disabled="!canEdit" class="w-8 h-8 border rounded-md cursor-pointer" :class="{ 'cursor-not-allowed opacity-50': !canEdit }" />
                          <input type="text" v-model="clinic.cor_botao" :readonly="!canEdit" class="flex-1 px-2 py-1 border rounded-md bg-white dark:bg-gray-900 text-xs input-font-color" :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }" />
                        </div>
                        <div class="flex items-center gap-2">
                          <label class="w-32 text-xs font-medium input-font-color">Cor do Ícone</label>
                          <input type="color" v-model="clinic.cor_icone" :disabled="!canEdit" class="w-8 h-8 border rounded-md cursor-pointer" :class="{ 'cursor-not-allowed opacity-50': !canEdit }" />
                          <input type="text" v-model="clinic.cor_icone" :readonly="!canEdit" class="flex-1 px-2 py-1 border rounded-md bg-white dark:bg-gray-900 text-xs input-font-color" :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }" />
                        </div>
                        <div class="flex items-center gap-2">
                          <label class="w-32 text-xs font-medium input-font-color">Cor da Fonte</label>
                          <input type="color" v-model="clinic.cor_fonte" :disabled="!canEdit" class="w-8 h-8 border rounded-md cursor-pointer" :class="{ 'cursor-not-allowed opacity-50': !canEdit }" />
                          <input type="text" v-model="clinic.cor_fonte" :readonly="!canEdit" class="flex-1 px-2 py-1 border rounded-md bg-white dark:bg-gray-900 text-xs input-font-color" :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }" />
                        </div>
                        <div class="flex items-center gap-2">
                          <label class="w-32 text-xs font-medium input-font-color">Cor Secundária</label>
                          <input type="color" v-model="clinic.cor_secundaria" :disabled="!canEdit" class="w-8 h-8 border rounded-md cursor-pointer" :class="{ 'cursor-not-allowed opacity-50': !canEdit }" />
                          <input type="text" v-model="clinic.cor_secundaria" :readonly="!canEdit" class="flex-1 px-2 py-1 border rounded-md bg-white dark:bg-gray-900 text-xs input-font-color" :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }" />
                        </div>
                        <div class="flex items-center gap-2">
                          <label class="w-32 text-xs font-medium input-font-color">Cor das Waves do Login (Primária)</label>
                          <input type="color" v-model="clinic.cor_wave_login" :disabled="!canEdit" class="w-8 h-8 border rounded-md cursor-pointer" :class="{ 'cursor-not-allowed opacity-50': !canEdit }" />
                          <input type="text" v-model="clinic.cor_wave_login" :readonly="!canEdit" class="flex-1 px-2 py-1 border rounded-md bg-white dark:bg-gray-900 text-xs input-font-color" :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }" />
                        </div>
                        <div class="flex items-center gap-2">
                          <label class="w-32 text-xs font-medium input-font-color">Cor das Waves do Login (Secundária)</label>
                          <input type="color" v-model="clinic.cor_wave_login_secundaria" :disabled="!canEdit" class="w-8 h-8 border rounded-md cursor-pointer" :class="{ 'cursor-not-allowed opacity-50': !canEdit }" />
                          <input type="text" v-model="clinic.cor_wave_login_secundaria" :readonly="!canEdit" class="flex-1 px-2 py-1 border rounded-md bg-white dark:bg-gray-900 text-xs input-font-color" :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }" />
                        </div>
                        <div class="flex items-center gap-2">
                          <label class="w-32 text-xs font-medium input-font-color">Gradiente Login (Primária)</label>
                          <input type="color" v-model="clinic.cor_gradiente_login_from" :disabled="!canEdit" class="w-8 h-8 border rounded-md cursor-pointer" :class="{ 'cursor-not-allowed opacity-50': !canEdit }" />
                          <input type="text" v-model="clinic.cor_gradiente_login_from" :readonly="!canEdit" class="flex-1 px-2 py-1 border rounded-md bg-white dark:bg-gray-900 text-xs input-font-color" :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }" />
                        </div>
                        <div class="flex items-center gap-2">
                          <label class="w-32 text-xs font-medium input-font-color">Gradiente Login (Secundária)</label>
                          <input type="color" v-model="clinic.cor_gradiente_login_via" :disabled="!canEdit" class="w-8 h-8 border rounded-md cursor-pointer" :class="{ 'cursor-not-allowed opacity-50': !canEdit }" />
                          <input type="text" v-model="clinic.cor_gradiente_login_via" :readonly="!canEdit" class="flex-1 px-2 py-1 border rounded-md bg-white dark:bg-gray-900 text-xs input-font-color" :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }" />
                        </div>
                        <div class="flex items-center gap-2">
                          <label class="w-32 text-xs font-medium input-font-color">Gradiente Login (Terciária)</label>
                          <input type="color" v-model="clinic.cor_gradiente_login_to" :disabled="!canEdit" class="w-8 h-8 border rounded-md cursor-pointer" :class="{ 'cursor-not-allowed opacity-50': !canEdit }" />
                          <input type="text" v-model="clinic.cor_gradiente_login_to" :readonly="!canEdit" class="flex-1 px-2 py-1 border rounded-md bg-white dark:bg-gray-900 text-xs input-font-color" :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }" />
                        </div>
                        <div class="flex items-center gap-2">
                          <label class="w-32 text-xs font-medium input-font-color">Gradiente Login (Radial)</label>
                          <input type="color" v-model="clinic.cor_gradiente_login_radial" :disabled="!canEdit" class="w-8 h-8 border rounded-md cursor-pointer" :class="{ 'cursor-not-allowed opacity-50': !canEdit }" />
                          <input type="text" v-model="clinic.cor_gradiente_login_radial" :readonly="!canEdit" class="flex-1 px-2 py-1 border rounded-md bg-white dark:bg-gray-900 text-xs input-font-color" :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }" />
                        </div>
                      </div>
                    </div>
                    <!-- Modo Escuro -->
                    <div class="flex-1 mode-card p-4 overflow-y-auto max-h-[70vh] min-w-[320px]">
                      <div class="sticky top-0 z-10 pb-2 mb-2">
                        <h3 class="mode-title">Modo Escuro</h3>
                      </div>
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
                        <div class="flex items-center gap-2">
                          <label class="w-32 text-xs font-medium input-font-color">Cor do Botão</label>
                          <input type="color" v-model="clinic.cor_botao_dark" :disabled="!canEdit" class="w-8 h-8 border rounded-md cursor-pointer" :class="{ 'cursor-not-allowed opacity-50': !canEdit }" />
                          <input type="text" v-model="clinic.cor_botao_dark" :readonly="!canEdit" class="flex-1 px-2 py-1 border rounded-md bg-white dark:bg-gray-900 text-xs input-font-color" :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }" />
                        </div>
                        <div class="flex items-center gap-2">
                          <label class="w-32 text-xs font-medium input-font-color">Cor do Ícone</label>
                          <input type="color" v-model="clinic.cor_icone_dark" :disabled="!canEdit" class="w-8 h-8 border rounded-md cursor-pointer" :class="{ 'cursor-not-allowed opacity-50': !canEdit }" />
                          <input type="text" v-model="clinic.cor_icone_dark" :readonly="!canEdit" class="flex-1 px-2 py-1 border rounded-md bg-white dark:bg-gray-900 text-xs input-font-color" :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }" />
                        </div>
                        <div class="flex items-center gap-2">
                          <label class="w-32 text-xs font-medium input-font-color">Cor da Fonte</label>
                          <input type="color" v-model="clinic.cor_fonte_dark" :disabled="!canEdit" class="w-8 h-8 border rounded-md cursor-pointer" :class="{ 'cursor-not-allowed opacity-50': !canEdit }" />
                          <input type="text" v-model="clinic.cor_fonte_dark" :readonly="!canEdit" class="flex-1 px-2 py-1 border rounded-md bg-white dark:bg-gray-900 text-xs input-font-color" :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }" />
                        </div>
                        <div class="flex items-center gap-2">
                          <label class="w-32 text-xs font-medium input-font-color">Cor Secundária</label>
                          <input type="color" v-model="clinic.cor_secundaria_dark" :disabled="!canEdit" class="w-8 h-8 border rounded-md cursor-pointer" :class="{ 'cursor-not-allowed opacity-50': !canEdit }" />
                          <input type="text" v-model="clinic.cor_secundaria_dark" :readonly="!canEdit" class="flex-1 px-2 py-1 border rounded-md bg-white dark:bg-gray-900 text-xs input-font-color" :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }" />
                        </div>
                        <div class="flex items-center gap-2">
                          <label class="w-32 text-xs font-medium input-font-color">Cor das Waves do Login (Primária - Escuro)</label>
                          <input type="color" v-model="clinic.cor_wave_login_dark" :disabled="!canEdit" class="w-8 h-8 border rounded-md cursor-pointer" :class="{ 'cursor-not-allowed opacity-50': !canEdit }" />
                          <input type="text" v-model="clinic.cor_wave_login_dark" :readonly="!canEdit" class="flex-1 px-2 py-1 border rounded-md bg-white dark:bg-gray-900 text-xs input-font-color" :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }" />
                        </div>
                        <div class="flex items-center gap-2">
                          <label class="w-32 text-xs font-medium input-font-color">Cor das Waves do Login (Secundária - Escuro)</label>
                          <input type="color" v-model="clinic.cor_wave_login_secundaria_dark" :disabled="!canEdit" class="w-8 h-8 border rounded-md cursor-pointer" :class="{ 'cursor-not-allowed opacity-50': !canEdit }" />
                          <input type="text" v-model="clinic.cor_wave_login_secundaria_dark" :readonly="!canEdit" class="flex-1 px-2 py-1 border rounded-md bg-white dark:bg-gray-900 text-xs input-font-color" :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }" />
                        </div>
                        <div class="flex items-center gap-2">
                          <label class="w-32 text-xs font-medium input-font-color">Gradiente Login (Primária - Escuro)</label>
                          <input type="color" v-model="clinic.cor_gradiente_login_from_dark" :disabled="!canEdit" class="w-8 h-8 border rounded-md cursor-pointer" :class="{ 'cursor-not-allowed opacity-50': !canEdit }" />
                          <input type="text" v-model="clinic.cor_gradiente_login_from_dark" :readonly="!canEdit" class="flex-1 px-2 py-1 border rounded-md bg-white dark:bg-gray-900 text-xs input-font-color" :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }" />
                        </div>
                        <div class="flex items-center gap-2">
                          <label class="w-32 text-xs font-medium input-font-color">Gradiente Login (Secundária - Escuro)</label>
                          <input type="color" v-model="clinic.cor_gradiente_login_via_dark" :disabled="!canEdit" class="w-8 h-8 border rounded-md cursor-pointer" :class="{ 'cursor-not-allowed opacity-50': !canEdit }" />
                          <input type="text" v-model="clinic.cor_gradiente_login_via_dark" :readonly="!canEdit" class="flex-1 px-2 py-1 border rounded-md bg-white dark:bg-gray-900 text-xs input-font-color" :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }" />
                        </div>
                        <div class="flex items-center gap-2">
                          <label class="w-32 text-xs font-medium input-font-color">Gradiente Login (Terciária - Escuro)</label>
                          <input type="color" v-model="clinic.cor_gradiente_login_to_dark" :disabled="!canEdit" class="w-8 h-8 border rounded-md cursor-pointer" :class="{ 'cursor-not-allowed opacity-50': !canEdit }" />
                          <input type="text" v-model="clinic.cor_gradiente_login_to_dark" :readonly="!canEdit" class="flex-1 px-2 py-1 border rounded-md bg-white dark:bg-gray-900 text-xs input-font-color" :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }" />
                        </div>
                        <div class="flex items-center gap-2">
                          <label class="w-32 text-xs font-medium input-font-color">Gradiente Login (Radial - Escuro)</label>
                          <input type="color" v-model="clinic.cor_gradiente_login_radial_dark" :disabled="!canEdit" class="w-8 h-8 border rounded-md cursor-pointer" :class="{ 'cursor-not-allowed opacity-50': !canEdit }" />
                          <input type="text" v-model="clinic.cor_gradiente_login_radial_dark" :readonly="!canEdit" class="flex-1 px-2 py-1 border rounded-md bg-white dark:bg-gray-900 text-xs input-font-color" :class="{ 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed': !canEdit }" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Form Actions -->
                <div class="mt-8 flex justify-end">
                  <button
                    v-if="canEdit"
                    class="px-6 py-2 clinic-btn text-white rounded-md transition-colors shadow-sm disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center gap-2"
                    @click="saveClinic"
                    :disabled="!isFormValid || isSaving"
                  >
                    <SaveIcon class="w-4 h-4" />
                    {{ isSaving ? 'Salvando...' : 'Salvar Configurações' }}
                  </button>
                </div>
              </div>
            </div>
          </div>



          <!-- Modal de confirmação de reset de estilização -->
          <ModalTemplate v-if="showResetModal" @close="showResetModal = false">
            <template #header>
              <div class="w-full text-center">Confirmação</div>
            </template>
            <template #body>
              <div class="text-center">
                <p class="mb-6">Tem certeza que deseja resetar a estilização para o padrão do sistema?</p>
                <div class="flex justify-center gap-4">
                  <button
                    class="px-4 py-2 clinic-btn text-white rounded-md transition-colors shadow-sm"
                    @click="onConfirmReset"
                  >
                    OK
                  </button>
                  <button
                    class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-md text-xs font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    @click="showResetModal = false"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </template>
          </ModalTemplate>
        </div>
      </div>
    </div>
  </template>
</BaseTemplate>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import BaseTemplate from '../templates/BaseTemplate.vue';
import apiClient from '@/services/authService';
import { useClinicStore } from '@/stores/settings/clinicStore';
import { usePermissions } from '@/composables/usePermissions';
import {
  Save as SaveIcon,
  
  Building as BuildingIcon,
  Palette as PaletteIcon
} from 'lucide-vue-next';
import ModalTemplate from '../templates/ModalTemplate.vue';
import BannerMessage from '@/components/BannerMessage.vue';

// Estado reativo
const activeTab = ref('clinica');
const isSaving = ref(false);
const errorMessage = ref('');
const loginLogoPreview = ref(`/styles/public/logo_login.png?${Date.now()}`)
const logoInputRef = ref(null);
const loginLogoInputRef = ref(null);
const showResetBanner = ref(false);
const showResetModal = ref(false);
const showBanner = ref(false);
const bannerType = ref('success');
const bannerMessage = ref('');

// Função para exibir banners
const showBannerMessage = (type, message) => {
  bannerType.value = type;
  bannerMessage.value = message;
  showBanner.value = true;
};

const DEFAULT_LOGO = '/styles/public/inovamed.png';

const baseTemplateLogoSrc = computed(() => {
  // Se o usuário importou uma logo customizada, mostra a customizada, senão mostra a padrão
  const logoPath = clinic.value.logo || DEFAULT_LOGO;
  return logoPath + '?' + Date.now();
});
const loginLogoSrc = computed(() => {
  const logoPath = loginLogoPreview.value || DEFAULT_LOGO;
  return logoPath + '?' + Date.now();
});

// Abas do formulário
const tabs = [
  { id: 'clinica', name: 'Clínica', icon: BuildingIcon },
  { id: 'estilizacao', name: 'Estilização', icon: PaletteIcon }
];

// Dados da clínica
const clinic = ref({
  nome: '',
  endereco: '',
  telefone: '',
  url_portal_paciente: '',
  cnes: '',
  logo: '/styles/public/logo.png',
  login_logo: '/styles/public/logo_login.png',
  cor_botao: '#059669',
  cor_icone: '#059669',
  cor_fonte: '#22223b',
  cor_botao_dark: '#009dff',
  cor_icone_dark: '#fd79ac',
  cor_fonte_dark: '#fff',
  cor_secundaria: '#fd79ac',
  cor_secundaria_dark: '#fd79ac',
  cor_wave_login: '#fbd5e5',
  cor_wave_login_dark: '#4d5b6a',
  cor_wave_login_secundaria: '#e9b6c9',
  cor_wave_login_secundaria_dark: '#b48bbd',
  cor_gradiente_login_from: '#fbd5e5',
  cor_gradiente_login_via: '#fbd5e5',
  cor_gradiente_login_to: '#fbd5e5',
  cor_gradiente_login_radial: '#fbd5e5',
  cor_gradiente_login_from_dark: '#23272f',
  cor_gradiente_login_via_dark: '#fbd5e5',
  cor_gradiente_login_to_dark: '#fbd5e5',
  cor_gradiente_login_radial_dark: '#fbd5e5',
});

// Computed properties
const isFormValid = computed(() => {
  return clinic.value.nome.trim() !== '' && clinic.value.endereco.trim() !== '';
});

// Computed para verificar se pode editar
const canEdit = computed(() => {
  return canEditClinicSettings.value || isSuperuser.value;
});

// Computed para verificar se pode visualizar
const canView = computed(() => {
  return canViewClinicSettings.value || canEditClinicSettings.value || isSuperuser.value;
});

// Instância do store
const clinicStore = useClinicStore();

// Instância do composable de permissões
const {
  canEditClinicSettings,
  canViewClinicSettings,
  isSuperuser
} = usePermissions();

// Métodos de API
async function fetchClinic() {
  try {
    errorMessage.value = '';
    const data = await clinicStore.fetchClinicData();
    console.log('Dados da clínica carregados:', data);
    clinic.value = { ...clinic.value, ...data };
    // Atualiza o preview da logo do login com timestamp para forçar refresh
    loginLogoPreview.value = (clinic.value.login_logo || '/styles/public/logo_login.png') + '?' + Date.now();
    console.log('Logo da clínica:', clinic.value.logo);
    console.log('Login logo da clínica:', clinic.value.login_logo);
  } catch (error) {
    console.error('Erro ao carregar configurações da clínica:', error);
    if (error.response?.status === 401) {
      showBannerMessage('error', 'Sessão expirada. Faça login novamente.');
    } else {
      showBannerMessage('error', 'Erro ao carregar configurações da clínica. Tente novamente.');
    }
  }
}

async function saveClinic() {
  if (!isFormValid.value || isSaving.value) return;
  
  isSaving.value = true;
  errorMessage.value = '';
  
  try {
    await clinicStore.updateClinic(clinic.value);
    await fetchClinic();
    showBannerMessage('success', 'Configurações salvas com sucesso!');
  } catch (error) {
    console.error('Erro ao salvar configurações:', error);
    if (error.response?.status === 401) {
      showBannerMessage('error', 'Sessão expirada. Faça login novamente.');
    } else if (error.response?.status === 400) {
      showBannerMessage('error', 'Dados inválidos. Verifique os campos obrigatórios.');
    } else {
      showBannerMessage('error', 'Erro ao salvar configurações. Tente novamente.');
    }
  } finally {
    isSaving.value = false;
  }
}

async function onLogoChange(e) {
  const file = e.target.files[0];
  if (!file) return;
  
  try {
    errorMessage.value = '';
    const formData = new FormData();
    formData.append('file', file);
    
    console.log('Fazendo upload da logo:', file.name);
    const response = await apiClient.post('/api/v1/clinicsettings/logo/', formData, { 
      headers: { 'Content-Type': 'multipart/form-data' } 
    });
    
    console.log('Resposta do upload da logo:', response.data);
    
    // Atualiza o campo logo para o caminho correto
    clinic.value.logo = '/styles/public/logo.png';
    
    // Atualiza o clinicStore também
    clinicStore.clinic.logo = '/styles/public/logo.png';
    clinicStore.saveToStorage();
    
    await fetchClinic();
    showBannerMessage('success', 'Logo atualizada com sucesso!');
  } catch (error) {
    console.error('Erro ao fazer upload da logo:', error);
    if (error.response?.status === 401) {
      showBannerMessage('error', 'Sessão expirada. Faça login novamente.');
    } else {
      showBannerMessage('error', 'Erro ao fazer upload da logo. Tente novamente.');
    }
  }
}

async function onLoginLogoChange(e) {
  const file = e.target.files[0];
  if (!file) return;
  
  try {
    errorMessage.value = '';
    const formData = new FormData();
    formData.append('file', file);
    
    console.log('Fazendo upload da logo do login:', file.name);
    const response = await apiClient.post('/api/v1/clinicsettings/login_logo/', formData, { 
      headers: { 'Content-Type': 'multipart/form-data' } 
    });
    
    console.log('Resposta do upload da logo do login:', response.data);
    
    // Atualiza o campo login_logo e o preview
    clinic.value.login_logo = '/styles/public/logo_login.png';
    loginLogoPreview.value = '/styles/public/logo_login.png';
    
    // Atualiza o clinicStore também
    clinicStore.clinic.login_logo = '/styles/public/logo_login.png';
    clinicStore.saveToStorage();
    
    await fetchClinic();
    showBannerMessage('success', 'Logo de login atualizada com sucesso!');
  } catch (error) {
    console.error('Erro ao fazer upload da logo do login:', error);
    if (error.response?.status === 401) {
      showBannerMessage('error', 'Sessão expirada. Faça login novamente.');
    } else {
      showBannerMessage('error', 'Erro ao fazer upload da logo do login. Tente novamente.');
    }
  }
}



function confirmResetStyle() {
  showResetModal.value = true;
}

async function onConfirmReset() {
  showResetModal.value = false;
  await resetStyle();
}

async function resetStyle() {
  // Cores padrão do print
  clinic.value.cor_botao = '#006eff';
  clinic.value.cor_icone = '#001eff';
  clinic.value.cor_fonte = '#000000';
  clinic.value.cor_secundaria = '#a9e5fe';
  clinic.value.cor_wave_login = '#2d6bfb';
  clinic.value.cor_wave_login_secundaria = '#0084ff';
  clinic.value.cor_gradiente_login_from = '#3582fd';
  clinic.value.cor_gradiente_login_via = '#8a7fff';
  clinic.value.cor_gradiente_login_to = '#a9e5fe';
  clinic.value.cor_gradiente_login_radial = '#47c1f5';

  clinic.value.cor_botao_dark = '#007bff';
  clinic.value.cor_icone_dark = '#009dff';
  clinic.value.cor_fonte_dark = '#ffffff';
  clinic.value.cor_secundaria_dark = '#4d5b6a';
  clinic.value.cor_wave_login_dark = '#003e80';
  clinic.value.cor_wave_login_secundaria_dark = '#014a98';
  clinic.value.cor_gradiente_login_from_dark = '#23272f';
  clinic.value.cor_gradiente_login_via_dark = '#014a98';
  clinic.value.cor_gradiente_login_to_dark = '#003e80';
  clinic.value.cor_gradiente_login_radial_dark = '#014a98';

  // Logos padrão - usando inovamed.png como padrão
  clinic.value.logo = '/styles/public/inovamed.png';
  clinic.value.login_logo = '/styles/public/inovamed.png';
  loginLogoPreview.value = '/styles/public/inovamed.png';
  
  // Salva no backend para garantir persistência
  await saveClinic();
  
  // Atualiza o clinicStore com os novos valores
  clinicStore.clinic = { ...clinicStore.clinic, ...clinic.value };
  clinicStore.saveToStorage();
  clinicStore.applyClinicStyles();
  
  // Banner de confirmação
  showBannerMessage('success', 'Estilização resetada para o padrão com sucesso!');
}

// Lifecycle
onMounted(async () => {
  // Verifica se o usuário tem permissão para visualizar
  if (!canView.value) {
    // Redireciona para uma página de acesso negado ou dashboard
    window.location.href = '/dashboard';
    return;
  }
  
  await fetchClinic();
});
</script>

<style>
@import 'tailwindcss/base';
@import 'tailwindcss/components';
@import 'tailwindcss/utilities';

.clinic-btn {
  background-color: var(--clinic-btn-bg) !important;
  color: #fff !important;
  transition: background 0.2s;
}
.clinic-btn:hover {
  background-color: var(--clinic-btn-bg-hover) !important;
}
.input-font-color, input, select, textarea {
  color: #000 !important;
}
input[type="color"] {
  color: #000 !important;
  border: 1px solid #bbb;
}
.dark .input-font-color, .dark input, .dark select, .dark textarea {
  color: #fff !important;
}
.dark input[type="color"] {
  color: #fff !important;
  border: 1px solid #888;
}

/* Força o texto do 'clique aqui para importar' */
.import-link {
  color: #000 !important;
}
.dark .import-link {
  color: #fff !important;
}

/* Card de modo claro/escuro */
.mode-card {
  background: rgba(0,0,0,0.04) !important;
  border-radius: 12px;
}
.dark .mode-card {
  background: rgba(0,0,0,0.35) !important;
}
.mode-title {
  color: #222 !important;
  font-weight: bold;
  font-size: 1.1rem;
}
.dark .mode-title {
  color: #fff !important;
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>